import React, { useState, useEffect } from 'react';
import { useTranslations } from 'use-intl';
import Select from '@/UIlibrary/Select/Select';
import Input from '@/UIlibrary/InputField/InputField';
import { Typography } from '@/UIlibrary/Typography/Typography';
import { Info } from 'react-feather';
import InputField from '@/UIlibrary/InputField/InputField';
import { COUNTRY_CODES, CountryCode, extractBaseNumber, formatPhoneNumber } from '../phoneUtils';
import { useChatStore } from '../useChat';
import ConfirmModal from '@/stories/Components/ConfirmModal/ConfirmModal';
import { useSupabaseStore } from '../supabaseStore';
import { ChatTag, WhatsAppComponentButtonTypes } from '../types';
import SelectSearch, { Option } from '@/UIlibrary/SelectSearch/SelectSearch';
import Checkbox from '@/UIlibrary/Checkbox/Checkbox';
import { removeTimestamp } from './utils';
import { useOrganizationId } from '../useOrganizationId';
interface Template {
	id: string;
	name: string;
	status: string;
	category: string;
	languages: Array<{
		code: string;
		name: string;
	}>;
	description: string;
	type?: WhatsAppComponentButtonTypes;
}

interface TemplateMessageModalProps {
	isOpen: boolean;
	onClose: () => void;
	isNewChat?: boolean;
	defaultPhone?: string;
	defaultName?: string;
	defaultTemplateSearch?: string;
	defaultTag?: string;
}

const PhoneInput: React.FC<{
	value: string;
	onChange: (value: string) => void;
	required?: boolean;
	countryCode?: CountryCode;
	defaultPhone?: string;
	onCountryChange?: (country: CountryCode) => void;
}> = ({
	value,
	onChange,
	required = false,
	countryCode = COUNTRY_CODES[0],
	onCountryChange,
	defaultPhone
}) => {
	const [isCountryListOpen, setIsCountryListOpen] = useState(false);
	const [hasUserInteracted, setHasUserInteracted] = useState(false);

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
		setHasUserInteracted(true);
		const input = e.target.value.replace(/\D/g, '').slice(0, countryCode.length);
		onChange(input);
	};

	return (
		<div className="relative">
			<div
				className="absolute left-0 top-0 bottom-0 flex items-center pl-3 gap-1 cursor-pointer"
				onClick={() => setIsCountryListOpen(!isCountryListOpen)}
			>
				<span className="text-lg">{countryCode.flag}</span>
				<span className="text-sm text-gray-600">+{countryCode.prefix}</span>
				<span className="text-gray-400 text-xs">▼</span>
			</div>

			{isCountryListOpen && (
				<div className="absolute left-0 top-full mt-1 bg-white rounded-md shadow-lg z-50 max-h-48 overflow-y-auto">
					{COUNTRY_CODES.map((country) => (
						<div
							key={country.code}
							className="px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-center gap-2"
							onClick={() => {
								onCountryChange?.(country);
								setIsCountryListOpen(false);
							}}
						>
							<span>{country.flag}</span>
							<span className="text-sm">+{country.prefix}</span>
							<span className="text-sm text-gray-600">{country.code}</span>
						</div>
					))}
				</div>
			)}
			<div className="pl-24">
				<InputField
					value={
						hasUserInteracted
							? formatPhoneNumber(value, countryCode)
							: formatPhoneNumber(defaultPhone ?? '', countryCode)
					}
					onChange={handleChange}
					placeholder={countryCode.format}
					required={required}
				/>
			</div>
		</div>
	);
};

const DEFAULT_LANGUAGE = 'es_MX';

const TemplateMessageModal: React.FC<TemplateMessageModalProps> = ({
	isOpen,
	onClose,
	isNewChat,
	defaultPhone,
	defaultName,
	defaultTemplateSearch,
	defaultTag
}) => {
	const t = useTranslations('Support');
	const supabase = useSupabaseStore((state) => state.supabase);
	const { selectedChat, tagLookup } = useChatStore();
	const { organizationId } = useOrganizationId(false);

	const [phone, setPhone] = useState(isNewChat ? '' : (selectedChat?.customer_phone ?? ''));
	const [name, setName] = useState(isNewChat ? '' : (selectedChat?.customer_name ?? ''));
	const [selectedTemplate, setSelectedTemplate] = useState('');
	const [selectedTag, setSelectedTag] = useState<string>(
		defaultTag || selectedChat?.tag || ChatTag.SOPORTE
	);
	const [templates, setTemplates] = useState<Template[]>([]);
	const [tags, setTags] = useState<string[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [isFetchingTemplates, setIsFetchingTemplates] = useState(true);
	const [error, setError] = useState('');
	const [showDescription, setShowDescription] = useState(false);
	const [showQuotes, setShowQuotes] = useState(false);
	const [showTemporary, setShowTemporary] = useState(false);
	const [selectedCountry, setSelectedCountry] = useState<CountryCode>(COUNTRY_CODES[0]);

	const selectedChatPhone = () => {
		const phoneToUse = selectedChat?.customer_phone
			? selectedChat.customer_phone.replace(/\D/g, '')
			: '';

		if (phoneToUse) {
			for (const country of COUNTRY_CODES) {
				if (phoneToUse.startsWith(country.prefix)) {
					setSelectedCountry(country);
					return isNewChat ? '' : extractBaseNumber(phoneToUse, country.prefix);
				}
			}
		}
		return '';
	};

	useEffect(() => {
		setPhone(selectedChatPhone());
		setSelectedTag(defaultTag || selectedChat?.tag || ChatTag.SOPORTE);
		setName(isNewChat ? '' : (selectedChat?.customer_name ?? ''));
	}, [isNewChat, selectedChat, defaultTag]);

	useEffect(() => {
		if (defaultName) {
			setName(defaultName);
		}
	}, [defaultName]);

	useEffect(() => {
		if (isOpen && supabase && organizationId) {
			fetchTemplates();
		}
	}, [isOpen, supabase, organizationId]);

	useEffect(() => {
		if (templates.length > 0 && !selectedTemplate) {
			setSelectedTemplate(templates[0].name);
			setShowDescription(true);
		}
	}, [templates, selectedTemplate]);

	const fetchTemplates = async () => {
		if (!supabase || !organizationId) return;

		setIsFetchingTemplates(true);
		try {
			const { data, error } = await supabase.functions.invoke('whatsapp-fetch-templates', {
				body: {
					language: DEFAULT_LANGUAGE,
					whatsapp_org_id: organizationId
				}
			});

			if (error) throw error;
			let templates = data.templates;
			templates = templates.filter((template: any) => template.name !== 'hello_world');
			setTemplates(templates);
			const uniqueTags = new Set(Object.values(tagLookup));
			setTags([...uniqueTags]);
		} catch (err) {
			setError(t('errorFetchingTemplates'));
			console.error('Error fetching templates:', err);
		} finally {
			setIsFetchingTemplates(false);
		}
	};

	const handleSendTemplate = async () => {
		if (!supabase) return;

		if (phone.replace(/\D/g, '').length !== selectedCountry.length) {
			setError(t('invalidPhoneLength', { length: selectedCountry.length }));
			return;
		}

		setIsLoading(true);
		setError('');

		try {
			const {
				data: { session },
				error: sessionError
			} = await supabase.auth.getSession();

			if (sessionError) throw sessionError;
			if (!session) throw new Error('No active session');

			const response = await supabase.functions.invoke('whatsapp-send-template', {
				body: {
					phone: `${selectedCountry.prefix}${phone.replace(/\D/g, '')}`,
					template: {
						id: selectedTemplateData?.id,
						description: selectedTemplateData?.description,
						name: selectedTemplateData?.name,
						type: selectedTemplateData?.type,
						language: DEFAULT_LANGUAGE
					},
					tag: selectedTag,
					language: DEFAULT_LANGUAGE,
					name,
					whatsapp_org_id: organizationId,
					sentBy: session.user.id
				}
			});

			if (response.error) throw new Error(response.error);

			handleClose();
		} catch (err) {
			console.error('Error sending template:', err);
			setError(t('errorSendingTemplate'));
		} finally {
			setIsLoading(false);
		}
	};

	const handleCountryChange = (country: CountryCode) => {
		setSelectedCountry(country);
		setPhone('');
	};

	const filterTemplates = () => {
		return templates.filter((template) => {
			if (showQuotes && !template.name.startsWith('cotiz')) return false;
			if (!showQuotes && template.name.startsWith('cotiz')) return false;
			if (showTemporary && !template.name.startsWith('temp')) return false;
			if (!showTemporary && template.name.startsWith('temp')) return false;
			return true;
		});
	};

	const templateOptions = filterTemplates().map((template) => ({
		value: template.name,
		label: removeTimestamp(template.name)
	}));

	const tagOptions = tags.map((tag) => ({
		value: tag,
		label: tag
	}));

	const selectedTemplateData = templates.find((t) => t.name === selectedTemplate);

	const handleTemplateChange = (selectedOption: Option | null) => {
		if (!selectedOption) return;
		setSelectedTemplate(selectedOption.value);
		if (isNewChat) {
			setSelectedTag(tagLookup[selectedOption.value] ?? ChatTag.SOPORTE);
		}
		setShowDescription(true);
	};

	useEffect(() => {
		if (defaultPhone) {
			setPhone(defaultPhone);
		}
	}, [defaultPhone]);

	const handleClose = () => {
		onClose();
		setPhone(selectedChatPhone());
		setName(isNewChat ? '' : (selectedChat?.customer_name ?? ''));
		setSelectedTemplate('');
		setSelectedTag(defaultTag || selectedChat?.tag || ChatTag.SOPORTE);
		setError('');
	};

	const templateFormContent = (
		<div className="space-y-4">
			{isFetchingTemplates ? (
				<div className="flex flex-col items-center justify-center py-8 space-y-4">
					<div className="w-8 h-8 border-4 border-gray-200 border-t-blue-500 rounded-full animate-spin"></div>
					<Typography color="subtitle" size="sm">
						{t('loadingTemplates')}
					</Typography>
				</div>
			) : (
				<>
					<div className="space-y-2">
						<label className="block text-sm font-medium text-gray-700">{t('phoneNumber')}</label>
						<PhoneInput
							value={phone}
							onChange={setPhone}
							required
							countryCode={selectedCountry}
							onCountryChange={handleCountryChange}
							defaultPhone={defaultPhone}
						/>
					</div>

					<Input
						label={t('customerName')}
						placeholder={t('customerNamePlaceholder')}
						value={name}
						onChange={(e) => setName(e.target.value)}
					/>

					<div className="space-y-2">
						<SelectSearch
							label={t('template')}
							placeholder={t('SearchTemplate')}
							isInitiallyOpen={false}
							options={templateOptions}
							onChange={(selectedOption) => handleTemplateChange(selectedOption)}
							disabled={isFetchingTemplates}
							initialSearchTerm={defaultTemplateSearch}
						/>

						<div className="flex items-center justify-end gap-5">
							<Checkbox
								checked={showTemporary}
								onChange={() => setShowTemporary(!showTemporary)}
								name="show-description"
								label={t('ShowTemporary')}
							/>
							<Checkbox
								checked={showQuotes}
								onChange={() => setShowQuotes(!showQuotes)}
								name="show-quotes"
								label={t('ShowQuotes')}
							/>
						</div>
						{selectedTemplateData && showDescription && (
							<div className="mt-2 p-3 bg-gray-50 rounded-md">
								<div className="flex items-start space-x-2">
									<Info className="w-5 h-5 text-gray-500 mt-0.5 flex-shrink-0" />
									<div className="space-y-1">
										<Typography size="sm" color="gray-600">
											{selectedTemplateData.description}
										</Typography>
									</div>
								</div>
							</div>
						)}
					</div>

					<div className="space-y-2">
						<Select
							label={t('tag')}
							name="tag"
							value={selectedTag}
							options={tagOptions}
							onChange={(e) => {
								setSelectedTag(e.target.value);
							}}
						/>
					</div>

					{error && (
						<Typography color="error" size="sm">
							{error}
						</Typography>
					)}
				</>
			)}
		</div>
	);

	return (
		<ConfirmModal
			isOpen={isOpen}
			onClose={handleClose}
			title={t('sendTemplateMessage')}
			subtitle={t('sendTemplateMessageDescription')}
			confirmActionText={isLoading ? t('sending') : t('sendTemplate')}
			onConfirm={handleSendTemplate}
			onCancel={handleClose}
			confirmBtnLoading={isLoading}
			confirmBtnTestId="sendTemplateBtn"
			cancelBtnTestId="cancelTemplateBtn"
			type="confirm"
			maxWidth={650}
		>
			{templateFormContent}
		</ConfirmModal>
	);
};

export default TemplateMessageModal;
